import React from 'react';
import { DashboardPermissionGuard, useDashboardFeatureFlags, useDashboardPermissions, } from '@components';
import { Banner, ButtonClean, ConfirmModal, Loader, Skeleton, Subsection, useModalContext, } from '@fourthwall/components';
import { AddSubscriptionModal, SubscriptionBadge, UpgradeSubscriptionButton, getSubscriptionPrice, usePastDueSubscriptionErrorBanner, useSubscription, useTopSubscriptionPlan, } from '@modules/Dashboard/modules/Subscriptions';
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString';
import * as S from './SubscriptionPlan.styled';
import { SubscriptionPerksTable } from './components/SubscriptionPerksTable/SubscriptionPerksTable';
export const SubscriptionPlan = () => {
    const { hasPermission } = useDashboardPermissions();
    const { dashboardFeatureFlags } = useDashboardFeatureFlags();
    const { open } = useModalContext();
    const { isLoading, canUpgradeSubscription, activeSubscription, pendingSubscription, cancelActiveSubscription, undoCancelActiveSubscription, } = useSubscription();
    const isActiveSubscriptionFree = activeSubscription?.plan.price.value === 0;
    const pastDueSubscriptionErrorBanner = usePastDueSubscriptionErrorBanner();
    const { topSubscriptionPlan } = useTopSubscriptionPlan();
    const canUserUpgradeSubscription = hasPermission('subscriptions.edit');
    const openAddSubscriptionModal = () => {
        open(AddSubscriptionModal, {
            source: 'settings_billing',
        });
    };
    const openCancelSubscriptionConfirmModal = () => {
        const title = activeSubscription?.activeTo ? (React.createElement(React.Fragment, null,
            "You will keep access to Pro plan until",
            ' ',
            React.createElement(TimezonedDateString, { date: activeSubscription.activeTo }))) : null;
        open(ConfirmModal, {
            title: 'Are you sure you want to cancel your subscription?',
            text: title,
            cancelLabel: 'Keep subscription',
            confirmAppearance: 'destructive',
            confirmLabel: 'Cancel subscription',
            onConfirm: async () => {
                await cancelActiveSubscription();
            },
        });
    };
    const openUndoCancelSubscriptionConfirmModal = () => {
        const title = activeSubscription?.activeTo ? (React.createElement(React.Fragment, null,
            "You will automatically renew Pro plan on",
            ' ',
            React.createElement(TimezonedDateString, { date: activeSubscription.activeTo, format: "DD MMM, YYYY" }))) : null;
        open(ConfirmModal, {
            title: 'Your subscription is scheduled to be canceled. Would you like to undo this?',
            text: title,
            cancelLabel: 'Keep canceled',
            confirmLabel: 'Renew subscription',
            onConfirm: async () => {
                await undoCancelActiveSubscription();
            },
        });
    };
    if (!dashboardFeatureFlags.subscriptions.enabled)
        return null;
    const renderUpgradeContent = () => {
        if (isLoading || !canUpgradeSubscription)
            return null;
        return (React.createElement(React.Fragment, null,
            React.createElement(S.SectionDescription, null,
                isActiveSubscriptionFree ? React.createElement("strong", null, "You\u2019re currently on the Free plan. ") : '',
                "Upgrade to Pro for just",
                ' ',
                getSubscriptionPrice({
                    subscriptionPlan: topSubscriptionPlan,
                    separatorType: 'short',
                }),
                ' ',
                "and get perks to help your brand grow."),
            React.createElement(S.ButtonsContainer, null,
                React.createElement(UpgradeSubscriptionButton, { source: "settings_billing", onClick: openAddSubscriptionModal }))));
    };
    const renderSubscriptionContent = () => {
        const subscription = pendingSubscription || activeSubscription;
        if (isLoading || canUpgradeSubscription || !subscription?.plan)
            return null;
        const getSubscriptionBanners = () => {
            if (pastDueSubscriptionErrorBanner.isActive) {
                return (React.createElement(S.BannerContainer, null,
                    React.createElement(Banner, { appearance: "danger" }, pastDueSubscriptionErrorBanner.content)));
            }
        };
        const getSubscriptionDescription = () => {
            if (pendingSubscription) {
                return (React.createElement(S.SubscriptionDescription, null,
                    "Waiting for payment to be confirmed ",
                    React.createElement(Loader, { size: "small" })));
            }
            if (!pendingSubscription &&
                activeSubscription &&
                activeSubscription.scheduledCancelledAtTheEndOfPeriod) {
                const activeCopy = activeSubscription.activeTo ? (React.createElement(React.Fragment, null,
                    "(Subscription active until",
                    ' ',
                    React.createElement(TimezonedDateString, { date: activeSubscription.activeTo, format: "DD MMM, YYYY" }),
                    ")")) : null;
                return React.createElement(React.Fragment, null,
                    "Pending cancellation ",
                    activeCopy);
            }
            if (!pendingSubscription && activeSubscription && activeSubscription.activeTo) {
                return (React.createElement(S.SubscriptionDescription, null,
                    "Next billing on",
                    ' ',
                    React.createElement(TimezonedDateString, { date: activeSubscription.activeTo, format: "DD MMM, YYYY" })));
            }
        };
        const getPlanButtons = () => {
            const buttons = [
                {
                    enabled: !!(!pendingSubscription &&
                        activeSubscription &&
                        !activeSubscription.scheduledCancelledAtTheEndOfPeriod &&
                        !!activeSubscription.plan.recurringPeriod &&
                        canUserUpgradeSubscription),
                    label: 'Cancel subscription',
                    appearance: 'primary',
                    onClick: openCancelSubscriptionConfirmModal,
                },
                {
                    enabled: !!(!pendingSubscription &&
                        activeSubscription &&
                        activeSubscription.scheduledCancelledAtTheEndOfPeriod &&
                        canUserUpgradeSubscription),
                    label: 'Reactivate subscription',
                    appearance: 'primary',
                    onClick: openUndoCancelSubscriptionConfirmModal,
                },
            ];
            const enabledButtons = buttons.filter((button) => button.enabled);
            if (enabledButtons.length === 0)
                return null;
            return (React.createElement(S.ButtonsContainer, null, enabledButtons.map((buttonProps, index) => {
                return React.createElement(ButtonClean, { key: index, ...buttonProps });
            })));
        };
        const getSubscriptionName = () => {
            const isCustom = !!activeSubscription?.plan.custom;
            const isFree = activeSubscription?.plan.price.value === 0;
            if (isCustom && isFree) {
                return React.createElement(S.SubscriptionName, null, "You\u2019re currently on the Custom free plan.");
            }
            return (React.createElement(SubscriptionBadge, { label: `${isCustom ? 'Custom ' : ''}Fourthwall Pro (${getSubscriptionPrice({
                    subscriptionPlan: activeSubscription?.plan,
                })})` }));
        };
        return (React.createElement(S.SubscriptionContainer, null,
            getSubscriptionBanners(),
            getSubscriptionName(),
            React.createElement("div", null, getSubscriptionDescription()),
            getPlanButtons()));
    };
    const renderSkeleton = () => {
        if (isLoading) {
            return (React.createElement(Skeleton, { gap: 16 },
                React.createElement(Skeleton.Shape, { width: "100%", height: 21 }),
                React.createElement(Skeleton.Shape, { width: "80%", height: 21 })));
        }
        return null;
    };
    return (React.createElement(DashboardPermissionGuard, { permission: "subscriptions.view" },
        React.createElement(Subsection, { title: "Your plan" },
            React.createElement(S.Content, null,
                renderSkeleton(),
                renderUpgradeContent(),
                renderSubscriptionContent(),
                React.createElement(SubscriptionPerksTable, null)))));
};
